import { useState } from 'react';
import { Button, Card } from './primitives';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import './AddEventModal.css';

export type CalendarEventResult = { kind: 'match' } | { kind: 'session'; id: string };

interface AddEventModalProps {
  teamId: string;
  coachId: string | null;
  /** Pre-fill the date (YYYY-MM-DD), e.g. the day the coach clicked. */
  defaultDate: string;
  onClose: () => void;
  onCreated: (result: CalendarEventResult) => void;
}

type EventType = 'TRAINING' | 'FRIENDLY' | 'LEAGUE' | 'CUP' | 'TOURNAMENT';

const TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: 'TRAINING', label: 'Training session' },
  { value: 'FRIENDLY', label: 'Friendly' },
  { value: 'LEAGUE', label: 'League match' },
  { value: 'CUP', label: 'Cup game' },
  { value: 'TOURNAMENT', label: 'Tournament' },
];

const NEUTRAL_CORNER = { technical_tactical: 25, physical: 25, psychological: 25, social: 25 };
const NEUTRAL_PHASE = { attacking: 25, defending: 25, transitions: 25, individual: 25 };

/**
 * Create a calendar event. Match types (friendly/league/cup/tournament) insert
 * a `games` row (mirrors Games.tsx + adds kickoff_time/venue); a training
 * session inserts a minimal `sessions` row using the same payload shape the
 * onboarding flow uses (so it satisfies the same column constraints).
 */
export function AddEventModal({ teamId, coachId, defaultDate, onClose, onCreated }: AddEventModalProps) {
  const [type, setType] = useState<EventType>('LEAGUE');
  const [title, setTitle] = useState(''); // opponent (match) or session title (training)
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState('');
  const [homeAway, setHomeAway] = useState<'HOME' | 'AWAY' | 'NEUTRAL'>('HOME');
  const [venue, setVenue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMatch = type !== 'TRAINING';

  const save = async () => {
    if (!date) { setError('Pick a date.'); return; }
    if (isMatch && !title.trim()) { setError('Opponent is required for a match.'); return; }
    if (isPlaceholderSupabase()) { setError('Connect a Supabase backend to add events.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (isMatch) {
        const { error: gErr } = await supabase.from('games').insert({
          team_id: teamId,
          opponent: title.trim() || 'TBC',
          date,
          home_away: homeAway,
          competition: type,
          venue: venue.trim() || null,
          kickoff_time: time ? new Date(`${date}T${time}`).toISOString() : null,
        });
        if (gErr) { setSaving(false); setError(gErr.message); return; }
        onCreated({ kind: 'match' });
        return;
      }
      // Training session — mirror the onboarding session insert shape.
      const dateIso = `${date}T${time || '18:00'}:00`;
      const { data, error: sErr } = await supabase
        .from('sessions')
        .insert({
          team_id: teamId,
          coach_id: coachId,
          date: dateIso,
          title: title.trim() || 'Training session',
          objectives: [],
          corner_coverage: NEUTRAL_CORNER,
          phase_coverage: NEUTRAL_PHASE,
          psych_social_intent: '',
          duration_mins: 60,
          status: 'DRAFT',
        })
        .select('id')
        .single();
      if (sErr || !data) { setSaving(false); setError(sErr?.message ?? 'Could not create the session.'); return; }
      onCreated({ kind: 'session', id: (data as { id: string }).id });
    } catch (err) {
      setSaving(false);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="add-event__backdrop" role="dialog" aria-modal="true" aria-label="Add a calendar event">
      <Card variant="opaque" className="add-event">
        <div className="add-event__head">
          <h2 className="add-event__title">Add to the calendar</h2>
          <Button variant="glass" size="touchline" onClick={onClose}>Close</Button>
        </div>

        <label className="add-event__field">
          <span>Type</span>
          <select value={type} onChange={(e) => setType(e.target.value as EventType)} data-testid="event-type">
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        <label className="add-event__field">
          <span>{isMatch ? 'Opponent' : 'Session title'}</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isMatch ? 'e.g. Aurora Lions U12' : 'e.g. Finishing under pressure'}
            autoFocus
          />
        </label>

        <div className="add-event__row">
          <label className="add-event__field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="add-event__field">
            <span>Time {isMatch ? '(kick-off)' : ''}</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </label>
        </div>

        {isMatch && (
          <div className="add-event__row">
            <label className="add-event__field">
              <span>Home / Away</span>
              <select value={homeAway} onChange={(e) => setHomeAway(e.target.value as 'HOME' | 'AWAY' | 'NEUTRAL')}>
                <option value="HOME">Home</option>
                <option value="AWAY">Away</option>
                <option value="NEUTRAL">Neutral</option>
              </select>
            </label>
            <label className="add-event__field">
              <span>Venue</span>
              <input type="text" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="optional" />
            </label>
          </div>
        )}

        {error && <p className="add-event__error">{error}</p>}

        <div className="add-event__actions">
          <Button variant="primary" size="touchline" onClick={() => void save()} disabled={saving} data-testid="event-save">
            {saving ? 'Adding…' : isMatch ? 'Add match' : 'Add session'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
